/**
 * MapPage - Top-level page for the map view of scheduled Work Orders
 * + Inspections.
 *
 * Mirrors CalendarPage's composition exactly so the user experience
 * across the two views feels symmetric — same filter bar at the top,
 * same click-to-popover affordance, same state hook.
 *
 * Differences from CalendarPage:
 *   - The visualization is MapView (Leaflet + Nominatim geocoding),
 *     which renders pins instead of a grid of date cells.
 *   - The "ungeocoded events" count is surfaced inline as a small
 *     informational banner so users understand why their N events
 *     don't all appear as pins (some addresses don't resolve).
 *   - Event clicks here can open the inline Leaflet popup directly,
 *     OR — for users who prefer the bigger modal — there's a
 *     fallback that surfaces the EventDetailsPopover in a Modal.
 *     We use the Modal path for parity with the Calendar.
 */
import React, { useState } from 'react';
import {
    EventFiltersBar,
    MapView,
    EventDetailsPopover,
    Modal,
} from '../../components/organisms';
import { Alert } from '../../components/atoms';
import { useScheduledEvents } from '../../hooks/useScheduledEvents';
import type { ScheduledEvent } from '../../services/scheduledEventsService';
import { PageContainer } from '../../components/templates/PageContainer';

const MapPage: React.FC = () => {
    const {
        filters,
        setFilters,
        events,
        isLoading,
        error,
        customerOptions,
        assigneeOptions,
        cityOptions,
        stateOptions,
    } = useScheduledEvents();

    /* Selected event drives the popover modal — same pattern as CalendarPage. */
    const [selectedEvent, setSelectedEvent] = useState<ScheduledEvent | null>(null);

    return (
        <PageContainer block="map-page" dataRef="page-map-page" width="wide">
            <div className="mb-16px">
                <h1 className="text-24px tablet:text-28px font-semibold text-brand-text">Map</h1>
                <p className="text-14px text-brand-text-muted mt-4px">
                    Where your scheduled work and inspections are happening.
                </p>
            </div>

            {/* Filter bar — same one used on CalendarPage */}
            <EventFiltersBar
                value={filters}
                onChange={setFilters}
                customerOptions={customerOptions}
                assigneeOptions={assigneeOptions}
                cityOptions={cityOptions}
                stateOptions={stateOptions}
                className="mb-16px"
            />

            {/* Loading / error banners */}
            {isLoading && (
                <div className="text-13px text-brand-text-muted py-8px">Loading events…</div>
            )}
            {error && (
                <Alert variant="error" className="mb-12px">{error}</Alert>
            )}

            {/* Map */}
            <MapView
                events={events}
                onSelectEvent={(event) => setSelectedEvent(event)}
            />

            {/* Detail modal — same component used on the Calendar */}
            <Modal
                isOpen={selectedEvent !== null}
                onClose={() => setSelectedEvent(null)}
                title="Event details"
                size="md"
            >
                {selectedEvent && (
                    /* No onClose — the Modal supplies its own X,
                     * matching the Calendar page's behavior. */
                    <EventDetailsPopover event={selectedEvent} />
                )}
            </Modal>
        </PageContainer>
    );
};

export default MapPage;
